import { useCallback, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { Contact, Funnel, Lead } from '../../../types';
import { notify } from '../../../utils/toast';
import { ChatConversation } from './useChat';
import {
    contactMatchesPhone,
    fetchContactById,
    fetchFirstStageIdForFunnel,
    findExistingContactByPhone
} from './contactPanelDeals.helpers';

interface UseCreateContactPanelDealParams {
    conversation: ChatConversation | undefined;
    currentContact: Contact | null;
    contacts: Contact[];
    organizationId?: string;
    selectedFunnelId: string | null;
    funnels: Funnel[];
    conversationTags: string[];
    conversationResponsible: string | null;
    createContact: (contact: Omit<Contact, 'id'>) => Promise<Contact>;
    refreshContacts: () => Promise<void>;
    refreshPanelLeads: () => Promise<void>;
    saveLead: (lead: Lead, meta?: { refreshFunnelId?: string | null }) => Promise<Lead | null>;
    setSelectedFunnelId: (funnelId: string) => void;
}

export function useCreateContactPanelDeal({
    conversation,
    currentContact,
    contacts,
    organizationId,
    selectedFunnelId,
    funnels,
    conversationTags,
    conversationResponsible,
    createContact,
    refreshContacts,
    refreshPanelLeads,
    saveLead,
    setSelectedFunnelId
}: UseCreateContactPanelDealParams) {
    const [isCreatingDeal, setIsCreatingDeal] = useState(false);

    const resolveContact = useCallback(async (phone: string): Promise<Contact | null> => {
        if (!organizationId) return null;

        if (
            currentContact?.id &&
            ((conversation?.contactId && currentContact.id === conversation.contactId) ||
                contactMatchesPhone(currentContact, phone))
        ) {
            return currentContact;
        }

        if (conversation?.contactId) {
            const contact = await fetchContactById(organizationId, conversation.contactId);
            if (contact) return contact;
        }

        return (
            currentContact ||
            contacts.find(contact => contactMatchesPhone(contact, phone)) ||
            await findExistingContactByPhone(organizationId, phone)
        );
    }, [organizationId, currentContact, conversation?.contactId, contacts]);

    const createOrResolveContact = useCallback(async (phone: string): Promise<Contact | null> => {
        const existingContact = await resolveContact(phone);
        if (existingContact) return existingContact;

        try {
            const isIg = conversation?.platform === 'instagram';
            const newContact = await createContact({
                name: conversation?.name || (isIg ? 'Instagram' : phone),
                phone: isIg ? '' : phone,
                whatsapp: isIg ? '' : phone,
                email: '',
            });
            await refreshContacts();
            return newContact;
        } catch (error) {
            const isLimitError =
                error instanceof Error && /limite de contatos/i.test(error.message);

            if (isLimitError) {
                throw error;
            }

            console.warn('[handleCreateDeal] Erro ao criar contato, pode já existir:', error);
            if (!organizationId) return null;

            const contact = await findExistingContactByPhone(organizationId, phone);
            if (contact) await refreshContacts();
            return contact;
        }
    }, [resolveContact, createContact, refreshContacts, conversation?.name, conversation?.platform, organizationId]);

    const handleCreateDeal = useCallback(async () => {
        if (!conversation || isCreatingDeal) return;

        const funnelIdForDeal = selectedFunnelId ?? funnels[0]?.id ?? null;
        if (!funnelIdForDeal || !organizationId) {
            notify.warning('Não foi possível definir o funil da negociação. Abra Negociações e selecione um funil ou aguarde o carregamento.');
            return;
        }

        try {
            setIsCreatingDeal(true);

            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.access_token) {
                notify.warning('Sua sessão expirou. Faça login novamente para criar a negociação.');
                return;
            }

            const defaultStageId = await fetchFirstStageIdForFunnel(organizationId, funnelIdForDeal);
            if (!defaultStageId) {
                notify.warning('Nenhuma etapa encontrada neste funil. Configure as etapas em Negociações.');
                return;
            }

            const isInstagram = conversation.platform === 'instagram';
            const igsid = isInstagram && conversation.id.startsWith('ig:')
                ? conversation.id.slice(3)
                : null;
            // WA: dígitos do JID; IG: IGSID (não há telefone no Direct)
            const phone = isInstagram
                ? (igsid || conversation.id)
                : conversation.id.replace(/\D/g, '');
            const contact = await createOrResolveContact(phone);
            if (!contact?.id) {
                notify.error('Não foi possível vincular um contato salvo. Tente novamente.');
                return;
            }

            // Purchase CAPI / Origin: lookup por contact_id na conversa Instagram
            if (organizationId) {
                await supabase
                    .from('chat_conversations')
                    .update({ contact_id: contact.id })
                    .eq('organization_id', organizationId)
                    .eq('remote_jid', conversation.id);
            }

            const isMetaAds = !!conversation.metaAttribution?.ctwaClid
                || (isInstagram && !!conversation.metaAttribution?.adId);
            const isGoogleAds = conversation.metaAttribution?.sourceId === 'google_ads'
                || !!conversation.metaAttribution?.googleClickId;
            const contactName = contact.name || conversation.name || (isInstagram ? 'Instagram' : phone);
            const adsOriginDetails = (isMetaAds || isGoogleAds) ? {
                    campaign: conversation.metaAttribution?.campaignName,
                    adSet: conversation.metaAttribution?.adsetName,
                    ad: conversation.metaAttribution?.adName
                } : undefined;
            const newLead: Lead = {
                id: `temp_${Date.now()}`,
                name: `Negociação - ${contactName}`,
                whatsApp: isInstagram ? '' : phone,
                email: contact.email || '',
                stage: defaultStageId,
                value: 0,
                date: new Date().toISOString(),
                entryDate: new Date().toISOString(),
                tags: conversationTags || [],
                origin: isMetaAds ? 'Meta Ads' : isGoogleAds ? 'Google Ads' : isInstagram
                    ? (conversation.metaAttribution?.origin || 'Instagram Direct')
                    : contact.origins?.name || 'WhatsApp',
                originDetails: adsOriginDetails,
                agent: conversationResponsible || null,
                contactId: contact.id
            };

            const saved = await saveLead(newLead, { refreshFunnelId: funnelIdForDeal });
            if (saved) {
                setSelectedFunnelId(funnelIdForDeal);
                notify.success('Negociação criada com sucesso!');
                await refreshPanelLeads();
            }
        } catch (error) {
            console.error('Error creating deal:', error);
            if (error instanceof Error && /limite de contatos/i.test(error.message)) {
                notify.error(error.message);
                return;
            }
            notify.error('Erro ao criar negociação. Tente novamente.');
        } finally {
            setIsCreatingDeal(false);
        }
    }, [
        conversation,
        isCreatingDeal,
        selectedFunnelId,
        funnels,
        organizationId,
        createOrResolveContact,
        conversationTags,
        conversationResponsible,
        saveLead,
        setSelectedFunnelId,
        refreshPanelLeads
    ]);

    return { isCreatingDeal, handleCreateDeal };
}
